import { TopFrame } from "@/components/dashboard/TopFrame";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LocalLawsTab } from "@/components/legal/LocalLawsTab";
import { PastCasesTab } from "@/components/legal/PastCasesTab";
import { CompareCountriesTab } from "@/components/legal/CompareCountriesTab";
import { useLegalTabs } from "@/hooks/useLegalTabs";

const Legal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const { data: legalTabs } = useLegalTabs();
  
  useEffect(() => {
    const checkUser = async () => {
      try {
        const {
          data: {
            session
          },
          error
        } = await supabase.auth.getSession();
        if (error) throw error;
        if (!session) {
          navigate('/');
          return;
        }
        setUser(session.user);
      } catch (error: any) {
        console.error('Error checking auth status:', error);
        toast.error(error.message || 'Error checking authentication status');
        navigate('/');
      }
    };
    checkUser();
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/');
        return;
      }
      setUser(session.user);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);
  
  // Get current tab settings
  const currentSettings = legalTabs?.[0];
  const showLocalLaws = currentSettings?.local_laws ?? true;
  const showPastCases = currentSettings?.past_cases ?? true;
  const showCompareCountries = currentSettings?.compare_country_laws ?? true;

  // Determine default tab
  const getDefaultTab = () => {
    if (showLocalLaws) return "local-laws";
    if (showPastCases) return "past-cases";
    if (showCompareCountries) return "compare-countries";
    return "local-laws"; // fallback
  };

  return <div className="min-h-screen bg-secondary">
      <TopFrame user={user} hideProgress />
      <div className="max-w-7xl mx-auto px-4 py-[24px]">
        <Tabs defaultValue={getDefaultTab()} className="w-full">
          <TabsList className="w-full justify-center bg-primary-DEFAULT">
            {showLocalLaws && (
              <TabsTrigger value="local-laws" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Local Laws
              </TabsTrigger>
            )}
            {showPastCases && (
              <TabsTrigger value="past-cases" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Past Cases and Precedents
              </TabsTrigger>
            )}
            {showCompareCountries && (
              <TabsTrigger value="compare-countries" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
                Compare Country Laws
              </TabsTrigger>
            )}
          </TabsList>
          
          {showLocalLaws && (
            <TabsContent value="local-laws">
              <LocalLawsTab />
            </TabsContent>
          )}
          {showPastCases && (
            <TabsContent value="past-cases">
              <PastCasesTab />
            </TabsContent>
          )}
          {showCompareCountries && (
            <TabsContent value="compare-countries">
              <CompareCountriesTab />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>;
};

export default Legal;
